import type { DropzoneInputProps } from 'react-dropzone'
import { RefreshCw, Trash2 } from 'lucide-react'
import { Button, cn } from '@teach/ui'
import { ImageWithSkeleton } from './ImageWithSkeleton'
import { UploadProgressOverlay } from './UploadProgressOverlay'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  value: string
  widescreen: boolean
  disabled: boolean
  /** Идёт загрузка (с любой стороны) — кнопки гаснут. */
  busy: boolean
  /** Процент для оверлея здесь (`null` — грузится с холста или не грузится). */
  shownProgress: number | null
  inputProps: DropzoneInputProps
  onReplace: () => void
  onRemove: () => void
}

/** Загруженная картинка в панели: превью и иконки «Заменить»/«Удалить». */
export const ImageUploadFilled = ({
  value,
  widescreen,
  disabled,
  busy,
  shownProgress,
  inputProps,
  onReplace,
  onRemove,
}: Props) => {
  const m = useMessages(messages)
  return (
    <div
      className={cn(
        'relative rounded-2xl border-2 border-dashed border-border bg-muted/30 text-center transition-colors hover:bg-muted/50',
        widescreen ? 'aspect-video overflow-hidden' : 'p-4',
      )}
      title={disabled ? m.noRights : undefined}
    >
      <ImageWithSkeleton
        src={value}
        rememberLoaded={false}
        className={
          widescreen
            ? 'size-full object-cover'
            : 'mx-auto max-h-56 w-auto rounded-xl object-contain'
        }
        skeletonClassName={widescreen ? 'size-full bg-muted' : 'h-40 w-full rounded-xl bg-muted'}
      />
      <input {...inputProps} />
      <UploadProgressOverlay progress={shownProgress} />
      {/* Заменить — тот же путь загрузки, что у пустой зоны; удалить — снять. */}
      <div className="absolute right-2 top-2 flex gap-1.5">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="rounded-lg"
          title={m.replaceImage}
          aria-label={m.replaceImage}
          onClick={onReplace}
          disabled={disabled || busy}
        >
          <RefreshCw className="size-4" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="rounded-lg"
          title={m.removeImage}
          aria-label={m.removeImage}
          onClick={onRemove}
          disabled={disabled || busy}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  )
}
